import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getLivePrice } from '../components/helpers';
import { buildVault, getFolders } from '../components/investments';
import {
  Page, Hero, StatStrip, ControlRail, Sheet, OptionList, Empty, Thumb, Meter,
  useReveal, MoreButton,
  findSkinImage, money, signed, pct, compactMoney,
} from './mobileShared';

// Vault — long-term holds, kept out of the Live tab entirely. Three levels, the
// same as the desktop: a folder you named, the positions inside it, and the
// individual units behind a position's count. The rule book is investments.js,
// so the phone can never disagree with the desktop about what is in here.

const SORTS = [
  { key: 'value', label: 'Biggest position' },
  { key: 'gain',  label: 'Best gain' },
  { key: 'loss',  label: 'Worst gain' },
  { key: 'age',   label: 'Held longest' },
  { key: 'qty',   label: 'Most units' },
];

const SORTERS = {
  value: (a, b) => b.value - a.value,
  gain:  (a, b) => (b.plPct ?? -Infinity) - (a.plPct ?? -Infinity),
  loss:  (a, b) => (a.plPct ?? Infinity) - (b.plPct ?? Infinity),
  age:   (a, b) => b.days - a.days,
  qty:   (a, b) => b.qty - a.qty,
};

const floatText = (f) => (f == null ? 'n/a' : Number(f).toFixed(6));
const shortDate = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });

export default function MobileInvestments({
  rows, settings, skinsData = [], livePrices,
  onSell, onEdit, onCreateFolder, onAssignFolder, onReturnToLive,
}) {
  const [query, setQuery]     = useState('');
  const [sort, setSort]       = useState('value');
  const [filters, setFilters] = useState(false);
  const [folder, setFolder]   = useState('all');   // which folder is being shown
  const [openPositions, setOpenPositions] = useState(() => new Set());
  const [moving, setMoving]   = useState(null);    // the unit whose move sheet is open

  const profC = 'var(--mb-pos)';
  const lossC = 'var(--mb-neg)';
  const pcol  = v => (v >= 0 ? profC : lossC);

  const folders = useMemo(() => getFolders(settings), [settings]);
  const vault = useMemo(
    () => buildVault({ rows, livePrices, settings, getPrice: getLivePrice }),
    [rows, livePrices, settings],
  );

  // A phone has no room for nested expanding folders, so the folder is a filter
  // across the top instead — same three levels, laid out flat.
  const group = useMemo(
    () => (folder === 'all' ? null : vault.groups.find(g => g.id === folder)),
    [vault.groups, folder],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = group ? group.positions : vault.positions;
    const list = q ? base.filter(p => p.skin.toLowerCase().includes(q)) : [...base];
    return list.sort(SORTERS[sort]);
  }, [vault.positions, group, query, sort]);

  const maxAbsPct = useMemo(
    () => Math.max(1, ...filtered.map(p => Math.abs(p.plPct ?? 0))),
    [filtered],
  );

  const [shown, hasMore, showMore] = useReveal(filtered, 12);

  const togglePosition = (key) => setOpenPositions(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const newFolder = () => {
    const name = window.prompt('Name this folder (e.g. Knives, Cases)');
    if (name && name.trim()) onCreateFolder?.(name.trim());
  };

  // Totals follow the folder filter: looking at Knives should say what the
  // knives are worth, not what the whole vault is worth.
  const totals = group || vault.totals;

  if (!vault.positions.length) {
    return (
      <Page>
        <Empty>
          The vault is empty. Move items here from Live and they leave that tab —
          an investment has one home.
        </Empty>
      </Page>
    );
  }

  return (
    <Page>
      <Hero
        label={group ? `${group.name} value` : 'Vault value'}
        value={compactMoney(totals.value)}
        tone={totals.pl >= 0 ? 'up' : 'down'}
        sub={<>
          <span style={{ color: pcol(totals.pl) }}>{signed(totals.pl)}</span>
          {totals.cost > 0 ? ` · ${pct((totals.pl / totals.cost) * 100)}` : ''}
        </>}
        foot={`${totals.count} position${totals.count !== 1 ? 's' : ''} · ${totals.units} unit${totals.units !== 1 ? 's' : ''}${totals.oldestDays > 0 ? ` · oldest ${totals.oldestDays}d` : ''}`}
      />

      <StatStrip items={[
        { label: 'Capital', value: money(totals.cost), sub: `across ${totals.units} unit${totals.units !== 1 ? 's' : ''}` },
        { label: 'Current value', value: money(totals.value),
          sub: totals.priced < totals.count ? `${totals.priced} of ${totals.count} priced` : 'at live prices' },
        { label: 'P/L', value: signed(totals.pl), color: pcol(totals.pl),
          sub: totals.cost > 0 ? pct((totals.pl / totals.cost) * 100) : 'n/a' },
        { label: 'Longest hold', value: `${totals.oldestDays}d`, sub: 'since first unit' },
      ]} />

      {/* ── Folder rail ─────────────────────────────
          Each chip carries its own P/L so the piles can be compared without
          opening them one at a time. */}
      <div className="mb-foldrail">
        <button type="button" className={`mb-foldchip${folder === 'all' ? ' on' : ''}`}
          onClick={() => setFolder('all')}>
          All<i>{vault.totals.units} units</i>
        </button>
        {vault.groups.map(g => (
          <button key={g.id} type="button" className={`mb-foldchip${folder === g.id ? ' on' : ''}`}
            onClick={() => setFolder(g.id)}>
            {/* The picture the folder was given on desktop, since it is the
                same folder and the chip is how it is recognised here. */}
            {g.image
              ? <img src={g.image} alt="" loading="lazy"
                  style={{ width: 18, height: 14, objectFit: 'contain', marginRight: 5, verticalAlign: 'middle' }} />
              : g.color && <span className="dot" style={{ background: g.color }} />}
            {g.name}
            <i style={{ color: g.priced ? pcol(g.pl) : undefined }}>
              {g.units} unit{g.units !== 1 ? 's' : ''}{g.priced ? ` · ${signed(g.pl)}` : ''}
            </i>
          </button>
        ))}
        {onCreateFolder && (
          <button type="button" className="mb-foldchip add" onClick={newFolder}>+ Folder</button>
        )}
      </div>

      <ControlRail
        query={query} onQuery={setQuery}
        placeholder="Search the vault…"
        activeCount={sort !== 'value' ? 1 : 0}
        onOpen={() => setFilters(true)}
      />

      {filtered.length === 0 ? (
        <Empty>{group ? 'This folder is empty.' : 'Nothing matches the search.'}</Empty>
      ) : (
        <>
          <div className="mb-list">
            {shown.map((p, i) => {
              const img = findSkinImage(skinsData, p.skin, p.cond);
              const open = openPositions.has(p.key);
              return (
                <motion.article
                  key={p.key}
                  className="mb-pos"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 8) * 0.03 }}
                >
                  <div className="mb-pos-top">
                    {/* The count is the way in to the actual items. */}
                    <button type="button" className="mb-pos-qty as-btn"
                      aria-expanded={open} onClick={() => togglePosition(p.key)}>
                      {p.qty}<i>unit{p.qty !== 1 ? 's' : ''}</i>
                    </button>
                    <Thumb src={img} />
                    <div className="mb-row-main">
                      <div className="mb-row-name">{p.skin}</div>
                      <div className="mb-row-meta">
                        <span>{p.cond || 'No condition'}</span>
                        <span>held {p.days}d</span>
                        <span>avg {money(p.avg)}</span>
                        {p.qty === 1 && p.units[0].float != null && (
                          <span className="mono">{floatText(p.units[0].float)}</span>
                        )}
                        {p.qty === 1 && p.units[0].paintSeed != null && (
                          <span className="mono">#{p.units[0].paintSeed}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {p.plPct != null && (
                    <Meter
                      ratio={Math.max(0.04, Math.abs(p.plPct) / maxAbsPct)}
                      color={p.plPct >= 0
                        ? 'linear-gradient(90deg, rgba(34,197,94,0.45), var(--green-dim))'
                        : 'linear-gradient(90deg, rgba(248,113,113,0.45), var(--red))'}
                      delay={0.12 + Math.min(i, 8) * 0.03}
                    />
                  )}

                  <div className="mb-pos-foot">
                    <div><i>Cost</i><b>{money(p.cost)}</b></div>
                    <div><i>Value</i><b>{p.price != null ? money(p.value) : 'n/a'}</b></div>
                    <div>
                      <i>P/L</i>
                      <b style={{ color: p.pl == null ? undefined : pcol(p.pl) }}>
                        {p.pl == null ? 'n/a' : signed(p.pl)}
                        {p.plPct != null && <em>{pct(p.plPct)}</em>}
                      </b>
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div className="mb-units"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}>
                        {p.units.map(u => (
                          <div key={u.row.id || u.realIndex} className="mb-unit">
                            <div className="mb-unit-id">
                              <span className="mono">{floatText(u.float)}</span>
                              <span className="mono dim">{u.paintSeed != null ? `#${u.paintSeed}` : 'no pattern'}</span>
                              <span className="dim">{shortDate(u.bought)} · {money(u.buy)}</span>
                            </div>
                            <div className="mb-unit-pl" style={{ color: u.pl == null ? undefined : pcol(u.pl) }}>
                              {u.pl == null ? 'n/a' : signed(u.pl)}
                            </div>
                            <div className="mb-unit-acts">
                              <button type="button" className="mb-mini sell"
                                onClick={() => onSell?.(u.row, u.realIndex)}>Sell</button>
                              <button type="button" className="mb-mini"
                                onClick={() => setMoving(u)}>Move</button>
                              <button type="button" className="mb-mini"
                                onClick={() => onEdit?.(u.row, u.realIndex)}>Edit</button>
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              );
            })}
          </div>
          {hasMore && <MoreButton onClick={showMore} remaining={filtered.length - shown.length} />}
        </>
      )}

      <Sheet open={filters} onClose={() => setFilters(false)} title="Sort positions">
        <OptionList label="Sort by" value={sort} onChange={v => { setSort(v); setFilters(false); }} options={SORTS} />
      </Sheet>

      {/* Moving a unit is a change of one property, so it is a list of the
          places it can go rather than a drag. "Back to Live" is on the same
          list because leaving the vault is the same kind of decision. */}
      <Sheet open={!!moving} onClose={() => setMoving(null)} title="Move this item">
        <OptionList
          label="Send it to"
          value={moving?.row?.investmentFolder || ''}
          onChange={v => {
            if (!moving) return;
            if (v === '__live__') onReturnToLive?.([moving.realIndex]);
            else onAssignFolder?.([moving.realIndex], v || null);
            setMoving(null);
          }}
          options={[
            { key: '', label: 'Unfiled' },
            ...folders.map(f => ({ key: f.id, label: f.name, color: f.color })),
            { key: '__live__', label: '← Back to Live' },
          ]}
        />
      </Sheet>
    </Page>
  );
}
